import React, { useState, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import "../style/VariacionPorPaisYMes.css"; // Reutilizamos este CSS

const VariacionPorProveedorMes = () => {
  const [rawData, setRawData] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("Chile");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [chartOptions, setChartOptions] = useState(null);
  const [loading, setLoading] = useState(true);

  // Estilos para los dropdowns
  const dropdownStyle = {
    backgroundColor: "#333",
    color: "#fff",
    border: "1px solid #555",
    borderRadius: "5px",
    padding: "8px",
    fontSize: "16px",
    marginRight: "10px",
    outline: "none",
    cursor: "pointer",
  };

  // Paleta de colores para las barras
  const colorList = [
    "#5470C6",
    "#91CC75",
    "#FAC858",
    "#EE6666",
    "#73C0DE",
    "#3BA272",
    "#FC8452",
    "#9A60B4",
    // Puedes añadir más colores si lo deseas
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch("https://marketbusters.pro/chart/graficos/fetch_proveedor_mes.php");
        const data = await response.json();
        setRawData(data);

        // Lista de países disponibles. Si existe "Chile", se usa por defecto.
        const countries = Object.keys(data);
        const defaultCountry = countries.includes("Chile") ? "Chile" : countries[0];
        setSelectedCountry(defaultCountry);

        // Para el país seleccionado, obtener el mes más reciente (orden descendente)
        const months = Object.keys(data[defaultCountry]).sort((a, b) => b.localeCompare(a));
        setSelectedMonth(months[0]);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!rawData || !selectedCountry || !selectedMonth) return;

    // Obtener proveedores para el país y mes seleccionados
    let providersData = rawData[selectedCountry][selectedMonth] || [];

    // Omitir proveedores con total_variacion = 0
    providersData = providersData.filter(
      (item) => parseInt(item.total_variacion.replace(/,/g, ""), 10) !== 0
    );

    // Ordenar de mayor a menor según total_variacion
    const sortedProviders = providersData.sort((a, b) => {
      const aVal = parseInt(a.total_variacion.replace(/,/g, ""), 10);
      const bVal = parseInt(b.total_variacion.replace(/,/g, ""), 10);
      return bVal - aVal;
    });

    // Extraer nombres y valores
    const providerNames = sortedProviders.map((item) => item.proveedor);
    const variations = sortedProviders.map((item) =>
      parseInt(item.total_variacion.replace(/,/g, ""), 10)
    );

    const maxChars = 10; // Número máximo de caracteres para truncar en el eje X

    // Convertimos cada valor en un objeto con su color
    const dataWithColors = variations.map((value, index) => ({
      value,
      itemStyle: {
        color: colorList[index % colorList.length],
      },
    }));

    const options = {
      backgroundColor: "#1E1E1E",
      title: {
        text: `Variación por Proveedor - ${selectedCountry} (${selectedMonth})`,
        left: "center",
        top: "5%",
        textStyle: {
          color: "#ffffff",
          fontSize: 18,
        },
      },
      // Tooltip para cada barra individual
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          // params.name = nombre de la categoría (proveedor)
          // params.value = valor numérico
          return `${params.name}<br/>${params.value}`;
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        top: "25%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: providerNames,
        axisLine: {
          lineStyle: { color: "#ffffff" },
        },
        axisLabel: {
          color: "#ffffff",
          interval: 0,
          rotate: 30,
          // Truncar nombres largos y añadir "..."
          formatter: (value) => {
            return value.length > maxChars ? value.slice(0, maxChars) + "..." : value;
          },
        },
      },
      yAxis: {
        type: "value",
        axisLine: {
          lineStyle: { color: "#ffffff" },
        },
        axisLabel: {
          color: "#ffffff",
        },
        splitLine: {
          lineStyle: { color: "#555555" },
        },
      },
      series: [
        {
          name: "Total Variación",
          type: "bar",
          data: dataWithColors, // Aquí asignamos nuestro arreglo con colores
        },
      ],
    };

    setChartOptions(options);
  }, [rawData, selectedCountry, selectedMonth]);

  if (loading) {
    return <p className="grafico-title">Cargando datos...</p>;
  }

  if (!rawData) {
    return <p className="grafico-title">No se pudieron cargar los datos.</p>;
  }

  // Opciones para los dropdowns
  const countryOptions = Object.keys(rawData);
  const monthOptions = rawData[selectedCountry]
    ? Object.keys(rawData[selectedCountry]).sort((a, b) => b.localeCompare(a))
    : [];

  return (
    <div className="grafico-container">
      {/* Fila superior con los dropdowns */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: "10px" }}>
        <select
          value={selectedCountry}
          onChange={(e) => {
            const newCountry = e.target.value;
            setSelectedCountry(newCountry);
            // Actualiza el mes por defecto para el nuevo país (el mes más reciente)
            const newMonths = Object.keys(rawData[newCountry]).sort((a, b) => b.localeCompare(a));
            setSelectedMonth(newMonths[0]);
          }}
          style={dropdownStyle}
        >
          {countryOptions.map((country) => (
            <option key={country} value={country}>
              {country}
            </option>
          ))}
        </select>
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={dropdownStyle}
        >
          {monthOptions.map((month) => (
            <option key={month} value={month}>
              {month}
            </option>
          ))}
        </select>
      </div>

      {/* Gráfico */}
      {chartOptions ? (
        <ReactECharts
          option={chartOptions}
          style={{
            height: "100%",
            width: "100%",
          }}
          theme="dark"
        />
      ) : (
        <p className="grafico-title">No hay datos para mostrar.</p>
      )}
    </div>
  );
};

export default VariacionPorProveedorMes;
